import { authReady, getToken, getUser, logout, resolveIsAdmin, supabase } from '../lib/auth.js';
import { deleteAlert, getBillingPortal, getProfile, listAlerts, listMyTrips, updateAlert, updateProfile, createCheckout } from '../lib/api.js';

const COUNTRY_PRESETS = [
  { id: 826, flag: 'GB', name: 'United Kingdom' },
  { id: 250, flag: 'FR', name: 'France' },
  { id: 724, flag: 'ES', name: 'Spain' },
  { id: 380, flag: 'IT', name: 'Italy' },
  { id: 620, flag: 'PT', name: 'Portugal' },
  { id: 528, flag: 'NL', name: 'Netherlands' },
  { id: 276, flag: 'DE', name: 'Germany' },
  { id: 208, flag: 'DK', name: 'Denmark' },
  { id: 372, flag: 'IE', name: 'Ireland' },
  { id: 392, flag: 'JP', name: 'Japan' },
  { id: 840, flag: 'US', name: 'United States' },
];

const root = document.querySelector('[data-account-page]');
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function show(el, visible = true) {
  if (el) el.hidden = !visible;
}

function setMessage(selector, message) {
  const el = root.querySelector(selector);
  if (!el) return;
  el.textContent = message;
  el.hidden = !message;
}

function emptyProfile(email) {
  return { uid: '', email: email || '', displayName: '', username: '', bio: '', avatarUrl: '', based: '', airport: '', airportCode: '', role: 'Traveller', publicProfile: false, countries: [] };
}

async function requireUser() {
  await authReady;
  const user = getUser();
  if (!user) {
    window.location.replace(`/login?next=${encodeURIComponent(window.location.pathname)}`);
    return null;
  }
  return user;
}

function initOverview(user) {
  root.querySelector('[data-auth-loading]')?.remove();
  root.querySelectorAll('[data-account-overview]').forEach((el) => show(el));
  root.querySelector('[data-user-email]').textContent = user.email || '';
  const name = user.user_metadata?.display_name;
  if (name) {
    const nameEl = root.querySelector('[data-user-name]');
    nameEl.textContent = name;
    show(nameEl);
  }
  root.querySelector('[data-logout]').hidden = false;
  root.querySelector('[data-logout]').addEventListener('click', async () => {
    await logout();
    window.location.href = '/';
  });
  resolveIsAdmin().then((isAdmin) => show(root.querySelector('[data-admin-card]'), isAdmin));
}

async function initAlerts(token) {
  const loading = root.querySelector('[data-auth-loading]');
  const list = root.querySelector('[data-alerts-list]');
  const empty = root.querySelector('[data-empty-alerts]');
  const render = (alerts) => {
    loading?.remove();
    list.innerHTML = '';
    show(empty, alerts.length === 0);
    alerts.forEach((alert) => {
      const el = document.createElement('article');
      el.className = 'bg-white rounded-xl border border-gray-100 p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4';
      el.innerHTML = `<div><p class="font-semibold">${esc(alert.flyFrom)} -> ${esc(alert.flyTo || 'Anywhere')}</p><p class="text-sm text-gray-500">${esc(alert.dateFrom)}${alert.dateTo ? ` - ${esc(alert.dateTo)}` : ''} · Max £${esc(alert.maxPrice)}</p><p class="text-xs text-gray-400 mt-1 capitalize">${esc(alert.status)}${alert.lastSeenPrice != null ? ` · Last seen £${Math.round(alert.lastSeenPrice)}` : ''}</p></div><div class="flex gap-2"></div>`;
      const actions = el.lastElementChild;
      if (alert.status !== 'triggered') {
        const pause = document.createElement('button');
        pause.type = 'button';
        pause.className = 'text-sm px-3 py-1.5 rounded-lg border border-gray-200';
        pause.textContent = alert.status === 'active' ? 'Pause' : 'Resume';
        pause.addEventListener('click', async () => {
          try {
            const updated = await updateAlert(token, alert.id, { status: alert.status === 'active' ? 'paused' : 'active' });
            alert.status = updated.status;
            render(alerts.map((a) => (a.id === alert.id ? updated : a)));
          } catch (err) { setMessage('[data-page-error]', err.message || 'Update failed'); }
        });
        actions.appendChild(pause);
      }
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'text-sm px-3 py-1.5 rounded-lg text-red-600 border border-red-100';
      del.textContent = 'Delete';
      del.addEventListener('click', async () => {
        try {
          await deleteAlert(token, alert.id);
          render(alerts.filter((a) => a.id !== alert.id));
        } catch (err) { setMessage('[data-page-error]', err.message || 'Delete failed'); }
      });
      actions.appendChild(del);
      list.appendChild(el);
    });
  };
  try {
    const data = await listAlerts(token);
    render(data.alerts || []);
  } catch (err) {
    loading?.remove();
    setMessage('[data-page-error]', err.message || 'Could not load alerts');
  }
}

async function initBilling(token) {
  root.querySelector('[data-auth-loading]')?.remove();
  show(root.querySelector('[data-billing-success]'), new URLSearchParams(window.location.search).get('success') === '1');
  const setBusy = (busy) => root.querySelectorAll('button').forEach((b) => { b.disabled = busy; });
  root.querySelectorAll('[data-checkout]').forEach((button) => {
    button.addEventListener('click', async () => {
      setBusy(true);
      try {
        const cfg = (window.__CONFIG__ && window.__CONFIG__.stripe) || {};
        const priceId = button.dataset.plan === 'starter' ? cfg.starterPriceId : cfg.proPriceId;
        const { url } = await createCheckout(token, priceId);
        window.location.href = url;
      } catch (err) {
        setBusy(false);
        setMessage('[data-page-error]', err.message || 'Checkout failed');
      }
    });
  });
  root.querySelector('[data-billing-portal]')?.addEventListener('click', async () => {
    setBusy(true);
    try {
      const { url } = await getBillingPortal(token);
      window.location.href = url;
    } catch (err) {
      setBusy(false);
      setMessage('[data-page-error]', err.message || 'Portal unavailable');
    }
  });
}

function renderAvatar(profile) {
  const box = root.querySelector('[data-avatar-preview]');
  if (!box) return;
  if (profile.avatarUrl) {
    box.innerHTML = `<img src="${esc(profile.avatarUrl)}" alt="" class="h-full w-full object-cover" />`;
  } else {
    box.textContent = (profile.displayName || profile.email || 'U').slice(0, 1).toUpperCase();
  }
}

async function initProfile(user, token) {
  const form = root.querySelector('[data-profile-form]');
  const tripsPanel = root.querySelector('[data-trips-panel]');
  const selectedCountries = new Map();
  const countryList = root.querySelector('[data-country-list]');
  const renderCountries = () => {
    countryList.innerHTML = '';
    COUNTRY_PRESETS.forEach((country) => {
      const active = selectedCountries.has(country.id);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'px-3 py-2 rounded-lg border text-sm font-medium transition-colors';
      button.style.borderColor = active ? '#00C2B0' : '#E5E7EB';
      button.style.background = active ? 'rgba(0,194,176,0.1)' : '#FFFFFF';
      button.style.color = active ? '#007F73' : '#374151';
      button.textContent = `${country.flag} ${country.name}`;
      button.addEventListener('click', () => {
        if (selectedCountries.has(country.id)) selectedCountries.delete(country.id);
        else selectedCountries.set(country.id, country);
        renderCountries();
      });
      countryList.appendChild(button);
    });
  };
  try {
    const [profileData, tripsData] = await Promise.all([getProfile(token), listMyTrips(token)]);
    const profile = { ...emptyProfile(user.email), ...profileData };
    selectedCountries.clear();
    (profile.countries || []).forEach((c) => selectedCountries.set(c.id, c));
    for (const [key, value] of Object.entries(profile)) {
      const field = form.elements[key];
      if (!field) continue;
      if (field.type === 'checkbox') field.checked = Boolean(value);
      else field.value = value || '';
    }
    renderAvatar(profile);
    renderCountries();
    const publicLink = root.querySelector('[data-public-profile-link]');
    if (publicLink) {
      if (profile.publicProfile && profile.username) {
        publicLink.textContent = `Public profile: /profile/${profile.username}`;
        publicLink.classList.remove('hidden');
      } else {
        publicLink.textContent = '';
        publicLink.classList.add('hidden');
      }
    }
    root.querySelector('[data-auth-loading]')?.remove();
    show(form);
    show(tripsPanel);
    const trips = tripsData.trips || [];
    root.querySelector('[data-trip-count]').textContent = trips.length;
    show(root.querySelector('[data-empty-trips]'), trips.length === 0);
    root.querySelector('[data-trip-list]').innerHTML = trips.map((trip) => `<a href="${esc(trip.shareUrl || '#')}" class="block rounded-xl border border-gray-100 px-4 py-3 hover:border-teal-200 transition-colors"><div class="font-semibold text-sm text-gray-900">${esc(trip.title)}</div><div class="text-xs text-gray-500 mt-1">${esc([trip.destination, trip.startDate, trip.endDate].filter(Boolean).join(' · ') || trip.id)} · ${esc(trip.visibility || 'private')}</div></a>`).join('');
  } catch (err) {
    root.querySelector('[data-auth-loading]')?.remove();
    setMessage('[data-page-error]', err.message || 'Could not load profile');
  }
  root.querySelector('[data-avatar-input]')?.addEventListener('change', async (event) => {
    const file = event.target.files?.[0];
    if (!file || !supabase) return;
    if (!file.type.startsWith('image/')) return setMessage('[data-page-error]', 'Choose an image file for your profile photo');
    const label = root.querySelector('[data-avatar-label]');
    label.textContent = 'Uploading...';
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `${user.id}/avatar.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(path, file, { cacheControl: '3600', upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      const saved = await updateProfile(token, { avatarUrl: `${data.publicUrl}?v=${Date.now()}` });
      renderAvatar(saved);
      setMessage('[data-page-message]', 'Profile photo updated');
    } catch (err) {
      setMessage('[data-page-error]', err.message || 'Could not upload profile photo');
    } finally {
      label.textContent = 'Upload profile photo';
    }
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const payload = {
      displayName: data.get('displayName') || '',
      username: data.get('username') || '',
      bio: data.get('bio') || '',
      based: data.get('based') || '',
      airport: data.get('airport') || '',
      airportCode: String(data.get('airportCode') || '').toUpperCase(),
      role: data.get('role') || 'Traveller',
      publicProfile: Boolean(data.get('publicProfile')),
      countries: Array.from(selectedCountries.values()),
    };
    try {
      const saved = await updateProfile(token, payload);
      renderAvatar(saved);
      setMessage('[data-page-error]', '');
      const publicLink = root.querySelector('[data-public-profile-link]');
      if (publicLink) {
        if (saved.publicProfile && saved.username) {
          publicLink.textContent = `Public profile: /profile/${saved.username}`;
          publicLink.classList.remove('hidden');
        } else {
          publicLink.textContent = '';
          publicLink.classList.add('hidden');
        }
      }
      setMessage('[data-page-message]', 'Profile saved');
    } catch (err) {
      setMessage('[data-page-error]', err.message || 'Could not save profile');
    }
  });
}

function initSettings(user) {
  root.querySelector('[data-auth-loading]')?.remove();
  root.querySelectorAll('[data-settings-content]').forEach((el) => show(el));
  root.querySelector('[data-current-email]').textContent = user.email || '';
  root.querySelector('[data-email-form]').addEventListener('submit', async (event) => {
    event.preventDefault();
    const email = new FormData(event.currentTarget).get('newEmail')?.trim();
    if (!supabase || !email) return;
    try {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
      setMessage('[data-email-error]', '');
      setMessage('[data-email-message]', 'Confirmation sent. Check your new inbox to verify the change.');
      event.currentTarget.reset();
    } catch (err) { setMessage('[data-email-error]', err.message || 'Could not update email'); }
  });
  root.querySelector('[data-password-form]').addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!supabase) return;
    const data = new FormData(event.currentTarget);
    const currentPassword = data.get('currentPassword');
    const newPassword = data.get('newPassword');
    const confirmPassword = data.get('confirmPassword');
    if (newPassword.length < 8) return setMessage('[data-password-error]', 'Password must be at least 8 characters');
    if (newPassword !== confirmPassword) return setMessage('[data-password-error]', 'Passwords do not match');
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword });
      if (signInError) return setMessage('[data-password-error]', 'Current password is incorrect');
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setMessage('[data-password-error]', '');
      setMessage('[data-password-message]', 'Password updated successfully');
      event.currentTarget.reset();
    } catch (err) { setMessage('[data-password-error]', err.message || 'Could not update password'); }
  });
}

async function initAccountPage() {
  const user = await requireUser();
  if (user) {
    const token = getToken();
    if (root.dataset.accountPage === 'overview') initOverview(user);
    if (root.dataset.accountPage === 'alerts') await initAlerts(token);
    if (root.dataset.accountPage === 'billing') await initBilling(token);
    if (root.dataset.accountPage === 'profile') await initProfile(user, token);
    if (root.dataset.accountPage === 'settings') initSettings(user);
  }
}

if (root) {
  void initAccountPage();
}
